/**
 * Provider brand marks and the tile that frames them.
 *
 * Every surface that lists an email/calendar provider previously drew its own
 * mark at its own size: Gmail inlined a raw 20px `<svg>`, Outlook rendered a
 * 20px `<img>` nudged with `mt-0.5`, and Connected Accounts fell back to a
 * letter monogram for Microsoft entirely. The result was three different
 * Microsofts and two different Googles across two pages.
 *
 * The marks live here once, and `ProviderLogoTile` gives them a shared frame so
 * cards line up regardless of the art inside: Google's "G" is a circle that
 * fills its viewBox, Microsoft's is a squared-off 2x2 grid, and unframed they
 * read as noticeably different weights sitting above identical card layouts.
 */

import type { ReactNode } from 'react';

/** Official Google "G". */
export function GoogleBrandMark({
  className = 'size-5',
}: {
  className?: string;
}) {
  return (
    <svg
      className={className}
      viewBox='0 0 24 24'
      role='img'
      aria-label='Google'
    >
      <path
        d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
        fill='#4285F4'
      />
      <path
        d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
        fill='#34A853'
      />
      <path
        d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
        fill='#FBBC05'
      />
      <path
        d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
        fill='#EA4335'
      />
    </svg>
  );
}

/**
 * Official Microsoft four-square mark.
 *
 * Inlined rather than loaded from `/assets/figma/microsoft-icon.svg` so it
 * renders identically on Connected Accounts, which resolves its other provider
 * logos from the Django `static/` origin and would otherwise need a second,
 * differently-based URL just for this one.
 */
export function MicrosoftBrandMark({
  className = 'size-5',
}: {
  className?: string;
}) {
  return (
    <svg
      className={className}
      viewBox='0 0 21 21'
      role='img'
      aria-label='Microsoft'
    >
      <rect x='0' y='0' width='10' height='10' fill='#F25022' />
      <rect x='11' y='0' width='10' height='10' fill='#7FBA00' />
      <rect x='0' y='11' width='10' height='10' fill='#00A4EF' />
      <rect x='11' y='11' width='10' height='10' fill='#FFB900' />
    </svg>
  );
}

/**
 * Official Attio logomark (the geometric "A").
 *
 * Path data extracted verbatim from attio.com's own site header, which renders
 * the mark in `currentColor` — monochrome is Attio's official treatment there,
 * so this component follows it rather than guessing a brand hex. Callers pick
 * the foreground via a text-color class.
 */
export function AttioBrandMark({
  className = 'size-5',
}: {
  className?: string;
}) {
  return (
    <svg
      className={className}
      viewBox='0 0 31.08 25.49'
      role='img'
      aria-label='Attio'
      fill='none'
    >
      <path
        d='M30.6468 17.7823L28.0599 13.6422C28.0599 13.6422 28.0503 13.6248 28.0445 13.6172L27.8405 13.2919C27.4555 12.674 26.7895 12.3045 26.062 12.3025L21.8949 12.2891L21.6042 12.7549L16.6249 20.7234L16.3496 21.1642L18.4361 24.4978C18.821 25.1176 19.487 25.4872 20.2203 25.4872H26.06C26.7799 25.4872 27.4613 25.108 27.8424 24.4998L28.0483 24.1706C28.0483 24.1706 28.056 24.161 28.058 24.1572L30.6487 20.0112C31.0741 19.3337 31.0741 18.4579 30.6487 17.7823H30.6468ZM29.8576 19.5166L27.2669 23.6625C27.2553 23.6817 27.2419 23.6971 27.2303 23.7125C27.1398 23.8146 27.0224 23.828 26.9705 23.828C26.9108 23.828 26.7645 23.8107 26.6702 23.6606L24.0795 19.5146C24.0506 19.4684 24.0256 19.4203 24.0025 19.3683C23.9794 19.3183 23.9621 19.2683 23.9467 19.2163C23.8889 19.0084 23.8889 18.7851 23.9467 18.5773C23.9755 18.4753 24.0198 18.3732 24.0775 18.2809L26.6644 14.1388C26.6644 14.1388 26.6683 14.133 26.6702 14.1291C26.7318 14.0367 26.8088 13.9944 26.8762 13.9809C26.9031 13.9732 26.9262 13.9713 26.9454 13.9675C26.9551 13.9675 26.9647 13.9675 26.9743 13.9675C27.034 13.9675 27.1822 13.9867 27.2746 14.1368L29.8615 18.277C30.0982 18.6543 30.0982 19.1393 29.8615 19.5166H29.8576Z'
        fill='currentColor'
      />
      <path
        d='M22.9913 7.7644C23.4148 7.08496 23.4148 6.21112 22.9913 5.53553L20.4044 1.39536L20.1889 1.04697C19.802 0.429125 19.136 0.0595703 18.4046 0.0595703H12.5649C11.8354 0.0595703 11.1694 0.429125 10.7806 1.0489L0.323361 17.7847C0.113561 18.1196 0 18.5065 0 18.8992C0 19.2918 0.111636 19.6787 0.321436 20.0117L3.12582 24.5022C3.5127 25.1219 4.17866 25.4896 4.90815 25.4896H10.7479C11.4812 25.4896 12.1472 25.12 12.5321 24.5002L12.7458 24.1615C12.7458 24.1615 12.7458 24.1615 12.7458 24.1576C12.7458 24.1576 12.7496 24.1519 12.7496 24.1499L14.8342 20.8162L21.0127 10.9287L22.9875 7.76633L22.9913 7.7644ZM22.3812 6.64996C22.3812 6.86361 22.3215 7.07919 22.2002 7.26974L11.9566 23.6649C11.8643 23.8131 11.716 23.8304 11.6564 23.8304C11.5967 23.8304 11.4504 23.8131 11.3561 23.6649L8.7673 19.517C8.53248 19.1417 8.53248 18.6586 8.7673 18.2794L19.0109 1.8881C19.1033 1.73797 19.2515 1.72064 19.3112 1.72064C19.3708 1.72064 19.519 1.73797 19.6134 1.89002L22.2002 6.03019C22.3215 6.22074 22.3812 6.43632 22.3812 6.64996V6.64996Z'
        fill='currentColor'
      />
    </svg>
  );
}

/**
 * Official Lightfield logomark (the four diagonal light-ray strokes).
 *
 * Path data extracted verbatim from lightfield.app's own header wordmark —
 * these four paths are the mark that closes the wordmark; the offset viewBox
 * crops to exactly that glyph without transforming the coordinates. Rendered
 * in `currentColor` to match the source site's monochrome treatment.
 */
export function LightfieldBrandMark({
  className = 'size-5',
}: {
  className?: string;
}) {
  return (
    <svg
      className={className}
      viewBox='69.16 0.12 13.06 11.32'
      role='img'
      aria-label='Lightfield'
      fill='none'
    >
      <path
        d='M74.8628 8.11111L69.1681 11.4342V9.93484L74.8628 6.61179V8.11111Z'
        fill='currentColor'
      />
      <path
        d='M82.215 9.93484V11.4342L76.5202 8.11111V6.61179L82.215 9.93484Z'
        fill='currentColor'
      />
      <path
        d='M74.8628 3.45001V4.94934L69.1681 1.62628V0.126953L74.8628 3.45001Z'
        fill='currentColor'
      />
      <path
        d='M82.215 1.62628L76.5202 4.94934V3.45001L82.215 0.126953V1.62628Z'
        fill='currentColor'
      />
    </svg>
  );
}

/**
 * A fixed 40px frame for a provider mark.
 *
 * Fixed size (not intrinsic) is the point: it guarantees the heading text of
 * every provider card starts at the same x-offset and that the header block has
 * the same height whether or not that provider has a subtitle.
 */
export function ProviderLogoTile({ children }: { children: ReactNode }) {
  return (
    <span className='flex size-10 shrink-0 items-center justify-center rounded-lg border border-border-secondary bg-bg-primary'>
      {children}
    </span>
  );
}
